import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play } from "@phosphor-icons/react";

/**
 * Cinematic AAA-style loader for House Rules.
 *
 * Sequence:
 *  1. Card-shuffle animation (3 cards rise + spread)
 *  2. Neon glow logo builds up + wordmark reveal
 *  3. Purple → pink → orange gradient progress bar sweeps
 *  4. Blinking "> PRESS START" - click or wait to enter
 *
 * Plays once per hard page load (module-level flag, resets on browser reload).
 * Client-side route changes do NOT replay it.
 */
const LOGO =
  "/assets/hero-logo.png";
const CARDS = [
  "/assets/deck-1.png",
  "/assets/deck-2.png",
  "/assets/deck-3.png",
];

// Module-level: play once per hard page load (not on client-side route changes)
let _loaderShown = false;

export default function Loader() {
  const [visible, setVisible] = useState(() => {
    if (typeof window === "undefined") return false;
    return !_loaderShown;
  });
  const [enterable, setEnterable] = useState(false);

  const dismiss = useCallback(() => {
    setVisible(false);
    _loaderShown = true;
    document.body.style.overflow = "";
  }, []);

  useEffect(() => {
    if (!visible) return;
    _loaderShown = true;
    document.body.style.overflow = "hidden";
    const t1 = setTimeout(() => setEnterable(true), 1600);
    const t2 = setTimeout(dismiss, 3600);
    const onKey = (e) => {
      if (e.key === "Enter" || e.key === " ") dismiss();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [visible, dismiss]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.button
          type="button"
          onClick={dismiss}
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, scale: 1.05 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-0 z-[9999] bg-[#080412] w-full h-full overflow-hidden cursor-pointer text-left flex items-center justify-center"
          data-testid="loader-screen"
          aria-label="Press start to enter"
        >
          {/* Ambient neon orbs */}
          <div aria-hidden className="absolute inset-0 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 0.9, scale: 1 }}
              transition={{ duration: 1.2, ease: "easeOut" }}
              className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[720px] h-[720px] rounded-full"
              style={{ background: "radial-gradient(circle, rgba(123,44,255,0.5), transparent 62%)", filter: "blur(30px)" }}
            />
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.2, delay: 0.3 }}
              className="absolute bottom-1/4 left-1/3 w-[460px] h-[460px] rounded-full"
              style={{ background: "radial-gradient(circle, rgba(255,44,140,0.4), transparent 65%)", filter: "blur(40px)" }}
            />
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.8 }}
              transition={{ duration: 1.2, delay: 0.5 }}
              className="absolute top-1/4 right-1/4 w-[380px] h-[380px] rounded-full"
              style={{ background: "radial-gradient(circle, rgba(41,108,255,0.35), transparent 65%)", filter: "blur(40px)" }}
            />
          </div>

          {/* Card shuffle (fanned) */}
          <div className="absolute inset-0 grid place-items-center pointer-events-none">
            {CARDS.map((c, i) => {
              const targetRot = (i - 1) * 14;
              const targetX = (i - 1) * 60;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 180, rotate: 0, scale: 0.5, x: 0 }}
                  animate={{
                    opacity: [0, 0.9, 0.9, 0],
                    y: [180, 0, 0, -60],
                    rotate: [0, targetRot, targetRot, targetRot + (i - 1) * 4],
                    scale: [0.5, 0.85, 0.85, 0.75],
                    x: [0, targetX, targetX, targetX * 1.1],
                  }}
                  transition={{
                    duration: 2.4,
                    delay: 0.15 + i * 0.2,
                    times: [0, 0.35, 0.7, 1],
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  className="absolute w-32 sm:w-36 aspect-[3/4] rounded-2xl overflow-hidden"
                  style={{ boxShadow: "0 24px 60px rgba(167,66,255,0.55), 0 0 0 1px rgba(255,255,255,0.15)" }}
                >
                  <img src={c} alt="" className="w-full h-full object-cover" />
                </motion.div>
              );
            })}
          </div>

          {/* Center logo - appears after cards */}
          <motion.div
            initial={{ opacity: 0, scale: 0.85, filter: "blur(24px)" }}
            animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
            transition={{ duration: 1, delay: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-10 px-6 text-center pointer-events-none"
          >
            <div className="relative inline-block">
              <div aria-hidden className="absolute -inset-10 rounded-full" style={{ background: "radial-gradient(circle, rgba(255,44,140,0.55), transparent 70%)", filter: "blur(40px)" }} />
              <motion.img
                src={LOGO}
                alt="House Rules"
                animate={{ filter: [
                  "drop-shadow(0 0 30px rgba(167,66,255,0.5))",
                  "drop-shadow(0 0 60px rgba(255,44,140,0.7))",
                  "drop-shadow(0 0 30px rgba(167,66,255,0.5))",
                ]}}
                transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
                className="relative h-32 sm:h-40 md:h-52 w-auto"
              />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.8 }}
              className="mt-6 font-sora font-medium text-[10.5px] sm:text-[11px] uppercase tracking-[0.42em] text-white/70"
            >
              Play by our house rules
            </motion.div>
          </motion.div>

          {/* Progress bar */}
          <div className="absolute bottom-24 left-1/2 -translate-x-1/2 h-[2px] w-56 sm:w-72 bg-white/8 overflow-hidden rounded-full pointer-events-none">
            <motion.div
              initial={{ x: "-100%" }}
              animate={{ x: "0%" }}
              transition={{ delay: 1.6, duration: 1.8, ease: "easeInOut" }}
              className="h-full w-full"
              style={{ background: "linear-gradient(90deg, #7B2CFF 0%, #FF2C8C 55%, #FF7A00 100%)" }}
            />
          </div>

          {/* Press start */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-3 pointer-events-none">
            {enterable && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4 }}
                className="flex items-center gap-3 font-sora font-semibold text-[10.5px] sm:text-[11px] uppercase tracking-[0.38em] text-white/85"
                data-testid="loader-press-start"
              >
                <span className="hidden sm:inline h-6 w-6 rounded-full grid place-items-center border border-white/40">
                  <Play size={10} weight="fill" className="ml-0.5 text-white" />
                </span>
                <motion.span
                  animate={{ opacity: [0.4, 1, 0.4] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  &gt; Press Start
                </motion.span>
              </motion.div>
            )}
          </div>

          {/* Corner meta */}
          <div className="absolute top-6 left-6 font-mono text-[9.5px] uppercase tracking-[0.4em] text-white/40 pointer-events-none">
            [ Loading Session ]
          </div>
          <div className="absolute top-6 right-6 font-mono text-[9.5px] uppercase tracking-[0.4em] text-brandpink pointer-events-none">
            House Rules · v1
          </div>
        </motion.button>
      )}
    </AnimatePresence>
  );
}
